import { useHasPermission } from "@/hooks/permissions/use-has-permission";
import ComponentTypePages from "@/pages/configurations/componentTypes";
import LadleHeatMetrics from "@/pages/configurations/ladleHeatMetrics";
import LadleTapTempAlertPage from "@/pages/configurations/ladleTapTempAlert";
import LocationPage from "@/pages/configurations/locations";
import PartsPage from "@/pages/configurations/parts";
import RootPage from "@/pages/configurations/roots";
import { Snapshots } from "@/pages/configurations/snapshots";
import TundishAlarmsPage from "@/pages/configurations/tundishAlarms";
import TundishHeatMetrics from "@/pages/configurations/tundishHeatMetrics";
import UpdateDressCache from "@/pages/configurations/updateDressCache";
import VendorsPage from "@/pages/configurations/vendors";
import WorkerTypesPage from "@/pages/configurations/workerTypes";
import WorkersPage from "@/pages/configurations/workers";
import XmlSettingsPage from "@/pages/configurations/xmlSettings";
import { MenuItem, MenuList } from "@material-ui/core";
import React, { ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";

type Component = {
	path: string;
	sidebar: () => ReactNode;
	main: () => ReactNode;
};

const components: Component[] = [
	{
		path: "workers",
		sidebar: () => <div>Workers</div>,
		main: () => <WorkersPage />,
	},
	{
		path: "workerTypes",
		sidebar: () => <div>Worker Types</div>,
		main: () => <WorkerTypesPage />,
	},
	{
		path: "vendors",
		sidebar: () => <div>Vendors</div>,
		main: () => <VendorsPage />,
	},
	{
		path: "component-types",
		sidebar: () => <div>ComponentType</div>,
		main: () => <ComponentTypePages />,
	},
	{
		path: "parts",
		sidebar: () => <div>Parts</div>,
		main: () => <PartsPage />,
	},
	{
		path: "locations",
		sidebar: () => <div>Locations</div>,
		main: () => <LocationPage />,
	},
	{
		path: "roots",
		sidebar: () => <>Roots</>,
		main: () => <RootPage />,
	},
	{
		path: "ladle-heat-metrics",
		sidebar: () => <>Ladle Heat Metrics</>,
		main: () => <LadleHeatMetrics />,
	},
	{
		path: "tundish-heat-metrics",
		sidebar: () => <>Tundish Heat Metrics</>,
		main: () => <TundishHeatMetrics />,
	},
	{
		path: "alarms/tundishes",
		sidebar: () => <>Tundish Alarms</>,
		main: () => <TundishAlarmsPage />,
	},
	{
		path: "alerts/ladle-tap-temp",
		sidebar: () => <>Ladle Tap Temp Alert</>,
		main: () => <LadleTapTempAlertPage />,
	},
	{
		path: "dress-cache",
		sidebar: () => <>Update Dress Cache</>,
		main: () => <UpdateDressCache />,
	},
	{
		path: "snapshots",
		sidebar: () => <span>Snapshots</span>,
		main: () => <Snapshots />,
	},
	{
		path: "xml-settings",
		sidebar: () => <>XML Feed Settings</>,
		main: () => <XmlSettingsPage />,
	},
];

const useIsSelected = () => {
	const { pathname } = useLocation();
	const current = pathname
		.replace(/^\/configuration\/?/, "")
		.replace(/\/$/, "");
	return (path: string) => current === path;
};

const SidebarItem = ({ path, label }: { path: string; label: string }) => {
	const isSelected = useIsSelected();
	return (
		<MenuItem selected={isSelected(path)} className="p-0">
			<Link to={`/configuration/${path}`} className="nav-link">
				{label}
			</Link>
		</MenuItem>
	);
};

const SidebarGroup = ({
	title,
	children,
}: { title: string; children: ReactNode }) => (
	<>
		<h3 className="mes-menu-group">{title}</h3>
		<MenuList className="mes-submenu">{children}</MenuList>
	</>
);

// First navigation entry in the repo gated by a permission. The page enforces it
// again on its own, and so does the API: hiding the link is not the control.
const DiagnosticsMenu = () => {
	if (!useHasPermission("Broker.GetSettings")) return null;
	return (
		<SidebarGroup title="Diagnostics">
			<SidebarItem path="xml-settings" label="XML Feed Settings" />
		</SidebarGroup>
	);
};

const sidebar = () => (
	<>
		<SidebarGroup title="Workers">
			<SidebarItem path="workers" label="Workers" />
		</SidebarGroup>
		<SidebarGroup title="Vendors">
			<SidebarItem path="vendors" label="Vendors" />
		</SidebarGroup>
		<SidebarGroup title="Parts">
			<SidebarItem path="roots" label="Roots" />
			<SidebarItem path="component-types" label="Component Types" />
			<SidebarItem path="parts" label="Parts" />
		</SidebarGroup>
		<SidebarGroup title="Locations">
			<SidebarItem path="locations" label="Locations" />
		</SidebarGroup>
		<SidebarGroup title="Heat Metrics">
			<SidebarItem path="ladle-heat-metrics" label="Ladle" />
			<SidebarItem path="tundish-heat-metrics" label="Tundish" />
		</SidebarGroup>
		<SidebarGroup title="Alarms & Alerts">
			<SidebarItem path="alarms/tundishes" label="Tundish Alarms" />
			<SidebarItem path="alerts/ladle-tap-temp" label="Ladle Tap Temp Alert" />
		</SidebarGroup>
		<SidebarGroup title="Data Tools">
			<SidebarItem path="dress-cache" label="Update Dress Cache" />
			<SidebarItem path="snapshots" label="Recover Snapshot" />
		</SidebarGroup>
		<DiagnosticsMenu />
	</>
);

const customConfigurations = [components, sidebar];

export { customConfigurations };
